import React, { useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  ActivityForm,
  ActivityFormActions,
} from "../../../schedulePlanning/components/ActivityForm";
import { useSearchParams } from "react-router-dom";
import { useScheduleEditor } from "../../../schedulePlanning";
import { useCapabilities } from "../../../../hooks/useCapabilities";

interface ContractorActivityDrawerProps {
  activityId: number;
  projectId: string | undefined;
  stageId?: number | null;
  handleClose: () => void;
}

export const ContractorActivityDrawer: React.FC<
  ContractorActivityDrawerProps
> = ({ activityId, projectId, stageId, handleClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { isAllowedToDeleteActivity } = useCapabilities();

  const {
    activityFormRef,
    activityFormData,
    activityDisabledConfig,
    editingScheduleItem,
    phaseOptionsActivity,
    isStageGateCompleted,
    isEstimateValidationStageGateCompleted,
    isLoading,
    isCommercialFocusActivity,
  } = useScheduleEditor(Number(projectId));

  // Override disabled config for contractor - allow only progress, description, and actualEndDate (when progress is 100) to be edited
  const contractorActivityDisabledConfig = {
    ...activityDisabledConfig,
    title: true,
    phase: true,
    category: true, // Restrict category for contractor
    plannedStartDate: true,
    plannedEndDate: true,
    actualEndDate: false, // Allow when progress is 100 (form logic: disabled={actualEndDate || progress !== 100})
    owner: true,
    progress: false, // Allow contractor to edit progress
    is_recurring: true,
    frequency: true,
    recurring_day: true,
    monthly_pattern: true,
    description: false, // Allow contractor to edit description
    asset_id: true,
  };

  const isActivityLocked = isStageGateCompleted && activityFormData.progress === 100 && activityFormData.phase === "Estimate/Validation";

  const canCommercialActivityDeleted = !isActivityLocked && Boolean(isAllowedToDeleteActivity);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentId = params.get("id");
    const currentType = params.get("type");

    if (currentId !== String(activityId) || currentType !== "activity") {
      params.set("id", String(activityId));
      params.set("type", "activity");
      setSearchParams(params, { replace: true });
    }
  }, [activityId]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!activityFormData) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <Typography>No activity data</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Form Content - Scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <ActivityForm
          isEditing={!!editingScheduleItem}
          ref={activityFormRef}
          onSave={() => {}}
          onCancel={() => {}}
          initialData={activityFormData}
          phaseOptions={phaseOptionsActivity}
          stageGateId={stageId}
          activityId={editingScheduleItem?.id}
          projectId={projectId}
          activityDisabledConfig={contractorActivityDisabledConfig}
          isStageGateCompleted={isStageGateCompleted}
          isEstimateValidationStageGateCompleted={
            isEstimateValidationStageGateCompleted
          }
          isContractorMode={true}
        />
      </Box>

      {/* Action Buttons - Fixed at Bottom */}
      <Box
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <ActivityFormActions
          onSave={() => activityFormRef.current?.submit()}
          onCancel={handleClose}
          onDelete={() => {}}
          isEditing={!!editingScheduleItem}
          isDeleteAllowed={canCommercialActivityDeleted}
          isEstimateValidationStageGateCompleted={
            isEstimateValidationStageGateCompleted
          }
          isCommercialFocusActivity={isCommercialFocusActivity}
        />
      </Box>
    </Box>
  );
};
